/**
 * Composable singleton de l'état du service (SD-4692) — modèle `maintenance/useMaintenance.ts`.
 *
 * Un seul sondage de `GET /service/status` par onglet (10 s visible, 30 s caché, relance immédiate au retour
 * de l'onglet) : il alimente le voyant ET le mode maintenance, qui s'y abonne. Deux échecs consécutifs
 * passent le voyant au gris ; un échec isolé conserve le dernier état lu.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue'

import { authFetch } from '@ggvie-consulting/auth/api'
import { config } from '@ggvie-consulting/config'
import {
  displayedLevel,
  parseServiceStatus,
  SERVICE_POLL_HIDDEN_MS,
  SERVICE_POLL_VISIBLE_MS,
  type ServiceLevel,
  type ServiceLoad,
  type ServiceSnapshot,
} from '@ggvie-consulting/service/logic'

export type ServiceSnapshotListener = (snapshot: ServiceSnapshot | null) => void

const snapshot: Ref<ServiceSnapshot | null> = ref(null)
const consecutiveFailures = ref(0)
const load: ComputedRef<ServiceLoad | null> = computed(() => snapshot.value?.load ?? null)
const level: ComputedRef<ServiceLevel> = computed(() => displayedLevel(load.value, consecutiveFailures.value))

const listeners = new Set<ServiceSnapshotListener>()
let pollTimer: ReturnType<typeof setTimeout> | null = null
let polling = false
let inflight: Promise<ServiceSnapshot | null> | null = null

async function fetchOnce(): Promise<ServiceSnapshot | null> {
  try {
    const response = await authFetch(`${config.api.baseURL}/v1/service/status`, { skipAuth: true })
    const parsed = response.ok ? parseServiceStatus(await response.json()) : null
    if (!parsed) {
      consecutiveFailures.value += 1
      return null
    }
    snapshot.value = parsed
    consecutiveFailures.value = 0
    return parsed
  } catch {
    consecutiveFailures.value += 1
    return null
  }
}

/** Lit l'état du service ; ne lève jamais (`null` = lecture échouée, le dernier état est conservé). */
export function refreshServiceStatus(): Promise<ServiceSnapshot | null> {
  if (!inflight) {
    inflight = fetchOnce().finally(() => {
      inflight = null
    })
  }
  return inflight
}

function isHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden'
}

function schedule(delayMs: number): void {
  if (pollTimer !== null) clearTimeout(pollTimer)
  pollTimer = setTimeout(() => {
    void tick()
  }, delayMs)
}

async function tick(): Promise<void> {
  pollTimer = null
  const result = await refreshServiceStatus()
  for (const listener of listeners) {
    try {
      listener(result)
    } catch (err) {
      console.warn('[service-status] abonné en échec', err)
    }
  }
  if (polling) schedule(isHidden() ? SERVICE_POLL_HIDDEN_MS : SERVICE_POLL_VISIBLE_MS)
}

function onVisibilityChange(): void {
  if (polling && !isHidden()) schedule(0)
}

/** Démarre le sondage (idempotent) : premier relevé immédiat. */
export function startServicePolling(): void {
  if (polling) return
  polling = true
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibilityChange)
  schedule(0)
}

export function stopServicePolling(): void {
  polling = false
  if (pollTimer !== null) clearTimeout(pollTimer)
  pollTimer = null
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibilityChange)
}

/** Abonnement aux relevés périodiques (mode maintenance) ; renvoie la fonction de désabonnement. */
export function onServiceSnapshot(listener: ServiceSnapshotListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export interface UseServiceStatus {
  snapshot: Ref<ServiceSnapshot | null>
  load: ComputedRef<ServiceLoad | null>
  level: ComputedRef<ServiceLevel>
  consecutiveFailures: Ref<number>
  refreshNow: () => Promise<ServiceSnapshot | null>
  startPolling: () => void
}

const singleton: UseServiceStatus = {
  snapshot,
  load,
  level,
  consecutiveFailures,
  refreshNow: refreshServiceStatus,
  startPolling: startServicePolling,
}

export function useServiceStatus(): UseServiceStatus {
  return singleton
}
